import React from 'react';
import { Box, FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import type { DigestPreferences, DigestSettingsType } from '../../api/config';
import { useDigestSettings } from '../../hooks/useDigestSettings';

/**
 * The four per-section digest toggles (restock / daily_usage / oos / rma).
 * Restock is locked-on and RMA is locked-off — both reflect server-side
 * reality:
 *   - turning restock off silently makes the digest job no-op for that
 *     location, which AMs hit by accident in the old permissive dashboard UI
 *   - RMA digests aren't wired up server-side yet
 * If either constraint changes, update `LOCKED_KEYS` / `LOCKED_VALUES` here.
 *
 * A section whose global digest type is turned off (sc-6504) is also
 * disabled, with a note saying so.
 */
const SECTION_KEYS = ['restock', 'daily_usage', 'oos', 'rma'] as const;
type SectionKey = (typeof SECTION_KEYS)[number];

const LOCKED_VALUES: Partial<Record<SectionKey, boolean>> = {
  restock: true,
  rma: false,
};

const SECTION_LABEL: Record<SectionKey, string> = {
  restock: 'Restock (Required)',
  daily_usage: 'Daily Usage',
  oos: 'Out of Stock',
  rma: 'RMA (Coming Soon)',
};

// Location section → the global digest type whose toggle switches it off for
// every location (sc-6504). The all_stocked toggle alone never disables the
// restock switch, and RMA has no global toggle.
const GLOBAL_TYPE: Partial<Record<SectionKey, DigestSettingsType>> = {
  restock: 'restock',
  daily_usage: 'usage',
  oos: 'oos',
};

export const GLOBALLY_OFF_NOTE =
  'Turned off globally for all locations (Edge Account Digests → Digest email settings).';

interface DigestPreferencesFieldsProps {
  value: Partial<DigestPreferences> | undefined;
  onChange: (patch: { digest_preferences: DigestPreferences }) => void;
  density?: 'standard' | 'compact';
}

// Server-side defaults from Location#default_digest_preferences. Used to
// promote a partial `digest_preferences` payload (e.g. an older row missing a
// field) to a fully-shaped `DigestPreferences` before emitting the patch.
const DEFAULT_PREFS: DigestPreferences = {
  restock: true,
  daily_usage: false,
  oos: false,
  rma: false,
};

export const DigestPreferencesFields: React.FC<DigestPreferencesFieldsProps> = ({
  value,
  onChange,
  density = 'standard',
}) => {
  const prefs = value ?? {};
  const switchSize = density === 'compact' ? 'small' : 'medium';
  // Shares the settings dialog's cache entry (see useDigestSettings). A failed
  // load leaves every switch enabled (the server gates at send regardless).
  const { data: settings } = useDigestSettings();

  const renderToggle = (key: SectionKey) => {
    const locked = key in LOCKED_VALUES;
    const globalType = GLOBAL_TYPE[key];
    const globallyOff = !!globalType && settings?.types[globalType]?.enabled === false;
    // A globally-off switch keeps showing the location's stored value, so
    // re-enabling the global toggle has a predictable effect.
    const checked = locked ? !!LOCKED_VALUES[key] : !!prefs[key];
    const label = globallyOff ? (
      <Box>
        <span>{SECTION_LABEL[key]}</span>
        <Typography variant="caption" color="text.secondary" display="block">
          {GLOBALLY_OFF_NOTE}
        </Typography>
      </Box>
    ) : SECTION_LABEL[key];
    return (
      <FormControlLabel
        key={key}
        control={
          <Switch
            size={switchSize}
            checked={checked}
            disabled={locked || globallyOff}
            onChange={e => onChange({
              digest_preferences: { ...DEFAULT_PREFS, ...prefs, [key]: e.target.checked },
            })}
          />
        }
        label={label}
        sx={density === 'compact' ? { mr: 1 } : undefined}
      />
    );
  };

  if (density === 'compact') {
    return (
      <Stack direction="row" spacing={0} flexWrap="wrap">
        {SECTION_KEYS.map(renderToggle)}
      </Stack>
    );
  }

  return <>{SECTION_KEYS.map(renderToggle)}</>;
};
